import { QuantityBreaksUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

class BundleBuilder extends HTMLElement {
  #steps = [];

  get #useAccordion() {
    return this.dataset.useAccordion !== 'false';
  }

  connectedCallback() {
    this.#steps = Array.from(this.querySelectorAll('[data-ref="bundle-builder-step"]'));

    this.#setStepIndexes();
    this.#organizeStepBlocks();
    this.#initPackFlavorSelectors();
    this.#bindStepSummaries();
    this.#bindPacks();
    this.#bindItems();
    if (this.#useAccordion) {
      if (this.#steps[0]) {
        this.#openStep(this.#steps[0], false);
      }
    } else {
      this.#openAllSteps(false);
    }
    this.#initializeDefaults();
    this.#syncState();
  }

  #setStepIndexes() {
    this.#steps.forEach((step, index) => {
      step.dataset.stepIndex = String(index);
    });
  }

  #organizeStepBlocks() {
    for (const step of this.#steps) {
      const packsWrapper = step.querySelector('[data-ref="bundle-builder-step-packs"]');
      const itemsWrapper = step.querySelector('.bundle-builder-step__items');
      const packBlocks = Array.from(step.querySelectorAll('[data-ref="bundle-builder-pack"]'));

      if (!packsWrapper || !itemsWrapper) continue;

      if (packBlocks.length === 0) {
        packsWrapper.hidden = true;
        continue;
      }

      packsWrapper.hidden = false;

      for (const packBlock of packBlocks) {
        packsWrapper.appendChild(packBlock);
      }
    }
  }

  #usesPackFlavorSelectors(step) {
    return step.dataset.usePackFlavorSelectors === 'true';
  }

  #packHasFlavorSelectors(pack) {
    return pack.dataset.showFlavorSelectors === 'true';
  }

  #selectedPackUsesFlavorSelectors(step) {
    const selectedPack = this.#getSelectedPack(step);
    return Boolean(selectedPack && this.#usesPackFlavorSelectors(step) && this.#packHasFlavorSelectors(selectedPack));
  }

  #getFlavorOptions(step) {
    return this.#getStepItems(step)
      .filter((item) => item.dataset.variantId)
      .map((item) => ({
        variantId: item.dataset.variantId,
        title: item.dataset.productTitle || 'Option',
        isFreeProduct: item.dataset.isFreeProduct === 'true',
      }));
  }

  #initPackFlavorSelectors() {
    for (const step of this.#steps) {
      if (!this.#usesPackFlavorSelectors(step)) continue;

      step.classList.add('bundle-builder-step--pack-flavor-selectors');

      const options = this.#getFlavorOptions(step);

      for (const pack of this.#getStepPacks(step)) {
        if (!this.#packHasFlavorSelectors(pack)) continue;
        this.#renderPackFlavorSelectors(pack, step, options);
      }
    }
  }

  #renderPackFlavorSelectors(pack, step, options) {
    const container = pack.querySelector('[data-ref="bundle-builder-pack-flavor-selectors"]');
    if (!container) return;

    const count = parseInt(pack.dataset.targetQuantity, 10) || 0;
    if (count <= 0 || options.length === 0) return;

    const label = container.dataset.selectorLabel || 'Choisir la saveur';
    container.replaceChildren();
    container.hidden = false;

    for (let index = 0; index < count; index += 1) {
      const wrapper = document.createElement('div');
      wrapper.className = 'bundle-builder-pack__flavor-selector';

      const labelEl = document.createElement('label');
      labelEl.className = 'bundle-builder-pack__flavor-selector-label';
      labelEl.textContent = count > 1 ? `${label} ${index + 1}` : label;

      const selectWrapper = document.createElement('div');
      selectWrapper.className = 'select-wrapper select-wrapper--full-width';

      const select = document.createElement('select');
      select.className = 'select select--full-width select--small';
      select.dataset.ref = 'bundle-builder-pack-flavor-select';
      select.dataset.selectorIndex = String(index);

      const emptyOption = document.createElement('option');
      emptyOption.value = '';
      emptyOption.textContent = '—';
      select.appendChild(emptyOption);

      for (const option of options) {
        const optionEl = document.createElement('option');
        optionEl.value = option.variantId;
        optionEl.textContent = option.title;
        optionEl.dataset.isFreeProduct = String(option.isFreeProduct);
        select.appendChild(optionEl);
      }

      const stopPackSelection = (event) => {
        event.stopPropagation();
      };

      select.addEventListener('click', stopPackSelection);
      select.addEventListener('keydown', stopPackSelection);
      select.addEventListener('change', () => {
        if (!pack.classList.contains('is-selected')) {
          this.#selectPack(step, pack);
          return;
        }

        this.#syncItemsFromPackFlavorSelectors(step, pack);
        this.#syncState();
        document.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });

      selectWrapper.appendChild(select);
      wrapper.appendChild(labelEl);
      wrapper.appendChild(selectWrapper);
      container.appendChild(wrapper);
    }
  }

  #getPackFlavorSelections(pack) {
    const selects = pack.querySelectorAll('[data-ref="bundle-builder-pack-flavor-select"]');

    return Array.from(selects)
      .map((select) => {
        const variantId = select.value;
        if (!variantId) return null;

        const option = select.selectedOptions[0];

        return {
          variantId,
          title: option?.textContent?.trim() || '',
          isFreeProduct: option?.dataset.isFreeProduct === 'true',
        };
      })
      .filter(Boolean);
  }

  #syncItemsFromPackFlavorSelectors(step, pack) {
    const selections = this.#getPackFlavorSelections(pack);
    const variantCounts = new Map();

    for (const selection of selections) {
      variantCounts.set(selection.variantId, (variantCounts.get(selection.variantId) || 0) + 1);
    }

    for (const item of this.#getStepItems(step)) {
      const quantity = variantCounts.get(item.dataset.variantId) || 0;
      this.#applyItemQuantity(item, quantity);
    }
  }

  #bindStepSummaries() {
    if (!this.#useAccordion) return;

    for (const step of this.#steps) {
      const summary = step.querySelector('.bundle-builder-step__summary');

      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        if (step.classList.contains('is-locked')) return;
        this.#openStep(step);
      });
    }
  }

  #bindPacks() {
    for (const step of this.#steps) {
      const packs = this.#getStepPacks(step);

      for (const pack of packs) {
        pack.addEventListener('click', (event) => {
          if (event.target.closest('[data-ref="bundle-builder-pack-flavor-selectors"]')) return;
          this.#selectPack(step, pack);
        });

        pack.addEventListener('keydown', (event) => {
          if (event.target.closest('[data-ref="bundle-builder-pack-flavor-selectors"]')) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;

          event.preventDefault();
          this.#selectPack(step, pack);
        });
      }
    }
  }

  #bindItems() {
    for (const step of this.#steps) {
      const stepType = step.dataset.stepType;
      const items = this.#getStepItems(step);

      if (stepType === 'single_select') {
        this.#syncSingleSelectButtonLabels(step);
      }

      for (const item of items) {
        const button = item.querySelector('[data-ref="bundle-builder-item-button"]');
        const minusButton = item.querySelector('[data-ref="bundle-builder-item-minus"]');
        const plusButton = item.querySelector('[data-ref="bundle-builder-item-plus"]');
        const input = item.querySelector('[data-ref="bundle-builder-item-input"]');

        if (stepType === 'cross_sell' && button) {
          button.textContent = button.dataset.labelPrice || button.textContent;
        }

        button?.addEventListener('click', (event) => {
          event.preventDefault();

          if (stepType === 'single_select') {
            this.#selectSingleItem(step, item);
          } else if (stepType === 'quantity_select') {
            return;
          } else {
            this.#toggleCrossSellItem(step, item);
          }
        });

        minusButton?.addEventListener('click', (event) => {
          event.preventDefault();
          const current = parseInt(input?.value, 10) || 0;
          this.#setItemQuantity(step, item, Math.max(0, current - 1));
        });

        plusButton?.addEventListener('click', (event) => {
          event.preventDefault();
          const current = parseInt(input?.value, 10) || 0;
          this.#setItemQuantity(step, item, current + 1);
        });

        input?.addEventListener('change', () => {
          const current = parseInt(input.value, 10) || 0;
          this.#setItemQuantity(step, item, Math.max(0, current));
        });
      }
    }
  }

  #syncSingleSelectButtonLabels(step) {
    const items = this.#getStepItems(step);
    const baseItem =
      items.find((item) => item.dataset.variantId && item.dataset.showPriceDifference !== 'true') ||
      items.find((item) => item.dataset.variantId);

    const basePrice = this.#getItemEffectivePrice(baseItem);

    for (const item of items) {
      const button = item.querySelector('[data-ref="bundle-builder-item-button"]');
      if (!button) continue;

      const defaultLabelAdd = button.dataset.labelAddDefault || button.dataset.labelAdd || button.textContent;
      const defaultLabelSelected = button.dataset.labelSelectedDefault || button.dataset.labelSelected || button.textContent;
      const customLabelAdd = button.dataset.labelAddCustom?.trim();
      const customLabelSelected = button.dataset.labelSelectedCustom?.trim();

      let labelAdd = customLabelAdd || defaultLabelAdd;

      if (!customLabelAdd && item.dataset.showPriceDifference === 'true') {
        const itemPrice = this.#getItemEffectivePrice(item);
        const difference = itemPrice - basePrice;

        if (difference > 0) {
          labelAdd = `${defaultLabelAdd} +${formatMoney(difference)}`;
        } else if (difference < 0) {
          labelAdd = `${defaultLabelAdd} -${formatMoney(Math.abs(difference))}`;
        }
      }

      button.dataset.labelAdd = labelAdd;
      button.dataset.labelSelected = customLabelSelected || defaultLabelSelected;

      if (item.dataset.selected !== 'true') {
        button.textContent = labelAdd;
      }
    }
  }

  #initializeDefaults() {
    for (const step of this.#steps) {
      const stepType = step.dataset.stepType;
      const items = this.#getStepItems(step);
      const packs = this.#getAvailableStepPacks(step);
      const preselectedItems = items.filter((item) => item.dataset.preselected === 'true' && item.dataset.variantId);

      if (packs.length > 0) {
        const preselectedPack = packs.find((pack) => pack.dataset.preselected === 'true');

        if (preselectedPack) {
          const targetQuantity = parseInt(preselectedPack.dataset.targetQuantity, 10) || 0;
          if (targetQuantity > 0) {
            step.dataset.manualPackQuantity = String(targetQuantity);
          }
        }
      }

      if (preselectedItems.length > 0) {
        if (this.#usesPackFlavorSelectors(step)) {
          this.#syncQuantitySelectItemsUi(step);
          continue;
        }

        if (stepType === 'single_select') {
          this.#selectSingleItem(step, preselectedItems[0], true);
        } else if (stepType === 'quantity_select') {
          for (const item of preselectedItems) {
            this.#setItemQuantity(step, item, 1, true);
          }
        } else {
          for (const item of preselectedItems) {
            this.#setCrossSellSelected(item, true, true);
          }
        }

        this.#syncQuantitySelectItemsUi(step);
        continue;
      }

      if (step.dataset.preselectFirstItem === 'true') {
        if (this.#usesPackFlavorSelectors(step)) {
          this.#syncQuantitySelectItemsUi(step);
          continue;
        }

        const firstAvailableItem = items.find((item) => item.dataset.variantId);
        if (firstAvailableItem) {
          if (stepType === 'single_select') {
            this.#selectSingleItem(step, firstAvailableItem, true);
          } else if (stepType === 'quantity_select') {
            this.#setItemQuantity(step, firstAvailableItem, 1, true);
          } else {
            this.#setCrossSellSelected(firstAvailableItem, true, true);
          }
        }
      }

      this.#syncQuantitySelectItemsUi(step);
    }
  }

  #syncQuantitySelectItemsUi(step) {
    if (step.dataset.stepType !== 'quantity_select') return;

    for (const item of this.#getStepItems(step)) {
      const current = parseInt(item.dataset.quantity, 10);
      const quantity = Number.isNaN(current) ? 0 : current;
      this.#applyItemQuantity(item, quantity);
    }
  }

  #openStep(targetStep, animate = true) {
    for (const step of this.#steps) {
      const shouldOpen = step === targetStep;

      if (animate) {
        this.#toggleStep(step, shouldOpen);
      } else {
        step.open = shouldOpen;
      }
    }
  }

  #openAllSteps(animate = false) {
    for (const step of this.#steps) {
      if (animate) {
        this.#toggleStep(step, true);
      } else {
        step.open = true;
      }
    }
  }

  #toggleStep(step, shouldOpen) {
    const content = step.querySelector('.bundle-builder-step__content');
    if (!content) {
      step.open = shouldOpen;
      return;
    }

    if (shouldOpen) {
      if (step.open) return;
      this.#animateStepOpen(step, content);
      return;
    }

    if (!step.open) return;
    this.#animateStepClose(step, content);
  }

  #animateStepOpen(step, content) {
    const targetHeight = content.scrollHeight;

    content.getAnimations().forEach((animation) => animation.cancel());

    step.open = true;
    content.style.overflow = 'hidden';
    content.style.height = '0px';
    content.style.opacity = '0';
    content.style.transform = 'translateY(-8px)';

    requestAnimationFrame(() => {
      const animation = content.animate(
        [
          { height: '0px', opacity: 0, transform: 'translateY(-8px)' },
          { height: `${targetHeight}px`, opacity: 1, transform: 'translateY(0)' },
        ],
        {
          duration: 380,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards',
        },
      );

      animation.onfinish = () => {
        content.style.height = '';
        content.style.opacity = '';
        content.style.transform = '';
        content.style.overflow = '';
      };
    });
  }

  #animateStepClose(step, content) {
    const startHeight = content.offsetHeight;

    content.getAnimations().forEach((animation) => animation.cancel());

    content.style.overflow = 'hidden';
    content.style.height = `${startHeight}px`;
    content.style.opacity = '1';
    content.style.transform = 'translateY(0)';

    requestAnimationFrame(() => {
      const animation = content.animate(
        [
          { height: `${startHeight}px`, opacity: 1, transform: 'translateY(0)' },
          { height: '0px', opacity: 0, transform: 'translateY(-8px)' },
        ],
        {
          duration: 300,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards',
        },
      );

      animation.onfinish = () => {
        step.open = false;
        content.style.height = '';
        content.style.opacity = '';
        content.style.transform = '';
        content.style.overflow = '';
      };
    });
  }

  #selectSingleItem(step, targetItem, silent = false) {
    for (const item of this.#getStepItems(step)) {
      this.#setSingleSelected(item, item === targetItem);
    }

    this.#syncState();
    if (!silent && this.#useAccordion) {
      window.setTimeout(() => {
        this.#openNextUnlockedStep(step);
      }, 420);
    }

    if (!silent) {
      document.dispatchEvent(new QuantityBreaksUpdatedEvent());
    }
  }

  #setSingleSelected(item, selected) {
    const button = item.querySelector('[data-ref="bundle-builder-item-button"]');
    if (!button) return;

    const labelAdd = button.dataset.labelAdd || button.textContent;
    const labelSelected = button.dataset.labelSelected || button.textContent;

    item.dataset.selected = String(selected);
    item.classList.toggle('is-selected', selected);
    button.classList.toggle('button--secondary', !selected);
    button.classList.toggle('button--primary', selected);
    button.textContent = selected ? labelSelected : labelAdd;
  }

  #applyItemQuantity(item, quantity) {
    const nextQuantity = Math.max(0, quantity);
    const input = item.querySelector('[data-ref="bundle-builder-item-input"]');
    const quantityWrapper = item.querySelector('[data-ref="bundle-builder-item-quantity"]');
    const button = item.querySelector('[data-ref="bundle-builder-item-button"]');

    if (!input || !quantityWrapper || !button) return;

    const step = item.closest('[data-ref="bundle-builder-step"]');
    const isQuantityStep = step?.dataset.stepType === 'quantity_select';

    input.value = nextQuantity;

    if (isQuantityStep) {
      quantityWrapper.hidden = false;
      button.hidden = true;
    } else {
      quantityWrapper.hidden = nextQuantity === 0;
      button.hidden = nextQuantity > 0;
    }

    item.dataset.quantity = String(nextQuantity);
    item.classList.toggle('is-selected', nextQuantity > 0);
  }

  #getStepMaximumAllowedQuantity(step) {
    if (step.dataset.stepType !== 'quantity_select') return null;

    const availablePacks = this.#getAvailableStepPacks(step);
    if (availablePacks.length === 0) return null;

    return availablePacks.reduce((maxQuantity, pack) => {
      const targetQuantity = parseInt(pack.dataset.targetQuantity, 10) || 0;
      return Math.max(maxQuantity, targetQuantity);
    }, 0);
  }

  #clampStepSelectedQuantityToAvailablePacks(step) {
    const maxAllowedQuantity = this.#getStepMaximumAllowedQuantity(step);
    if (!maxAllowedQuantity || maxAllowedQuantity <= 0) return;

    let overflowQuantity = this.#getStepSelectedQuantity(step) - maxAllowedQuantity;
    if (overflowQuantity <= 0) return;

    const items = this.#getStepItems(step).slice().reverse();

    for (const item of items) {
      const currentQuantity = parseInt(item.dataset.quantity, 10) || 0;
      if (currentQuantity <= 0) continue;

      const nextQuantity = Math.max(0, currentQuantity - overflowQuantity);
      overflowQuantity -= currentQuantity - nextQuantity;
      this.#applyItemQuantity(item, nextQuantity);

      if (overflowQuantity <= 0) break;
    }
  }

  #setItemQuantity(step, item, quantity, silent = false) {
    const maxAllowedQuantity = this.#getStepMaximumAllowedQuantity(step);
    const nextQuantity = maxAllowedQuantity ? Math.min(Math.max(0, quantity), maxAllowedQuantity) : Math.max(0, quantity);

    this.#applyItemQuantity(item, nextQuantity);

    this.#syncState();

    if (!silent) {
      document.dispatchEvent(new QuantityBreaksUpdatedEvent());
    }
  }

  #toggleCrossSellItem(step, item) {
    const isSelected = item.dataset.selected === 'true';
    this.#setCrossSellSelected(item, !isSelected);
    this.#syncState();
    if (this.#useAccordion) {
      this.#openNextUnlockedStep(step);
    }
    document.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #setCrossSellSelected(item, selected, silent = false) {
    const button = item.querySelector('[data-ref="bundle-builder-item-button"]');
    if (!button) return;

    const labelAdd = button.dataset.labelAdd || button.textContent;
    const labelSelected = button.dataset.labelSelected || button.textContent;
    const labelPrice = button.dataset.labelPrice || labelAdd;
    const step = item.closest('[data-ref="bundle-builder-step"]');
    const stepType = step?.dataset.stepType;

    item.dataset.selected = String(selected);
    item.classList.toggle('is-selected', selected);
    button.classList.toggle('button--secondary', !selected);
    button.classList.toggle('button--primary', selected);
    button.textContent = stepType === 'cross_sell' ? (selected ? labelSelected : labelPrice) : selected ? labelSelected : labelAdd;

    if (!silent) {
      this.#syncState();
    }
  }

  #openNextUnlockedStep(currentStep) {
    const currentIndex = parseInt(currentStep.dataset.stepIndex, 10);
    const nextStep = this.#steps[currentIndex + 1];

    if (nextStep && !nextStep.classList.contains('is-locked')) {
      this.#openStep(nextStep);
    }
  }

  #getStepItems(step) {
    return Array.from(step.querySelectorAll('[data-ref="bundle-builder-item"]'));
  }

  #getItemEffectivePrice(item) {
    if (item?.dataset.isFreeProduct === 'true') return 0;
    return parseInt(item?.dataset.price, 10) || 0;
  }

  #getItemEffectiveCompareAtPrice(item) {
    if (item?.dataset.isFreeProduct === 'true') return 0;
    return parseInt(item?.dataset.compareAtPrice, 10) || parseInt(item?.dataset.price, 10) || 0;
  }

  #getStepPacks(step) {
    return Array.from(step.querySelectorAll('[data-ref="bundle-builder-pack"]'));
  }

  #getAvailableStepPacks(step) {
    const packs = this.#getStepPacks(step);

    for (const pack of packs) {
      pack.hidden = false;
      pack.style.display = '';
      pack.setAttribute('aria-hidden', 'false');
    }

    return packs;
  }

  #getStepSelectedQuantity(step) {
    const stepType = step.dataset.stepType;

    if (stepType === 'quantity_select') {
      if (this.#selectedPackUsesFlavorSelectors(step)) {
        return this.#getPackFlavorSelections(this.#getSelectedPack(step)).length;
      }

      return this.#getStepItems(step).reduce((total, item) => total + (parseInt(item.dataset.quantity, 10) || 0), 0);
    }

    return this.#getStepItems(step).filter((item) => item.dataset.selected === 'true').length;
  }

  #getStepRequiredQuantity(step) {
    const effectiveTargetQuantity = parseInt(step.dataset.requiredPackQuantity, 10) || 0;
    if (effectiveTargetQuantity > 0) return effectiveTargetQuantity;

    const manualTargetQuantity = parseInt(step.dataset.manualPackQuantity, 10) || 0;
    return manualTargetQuantity;
  }

  #getSelectedPackVariant(step) {
    const selectedPack = this.#getStepPacks(step).find((pack) => !pack.hidden && pack.classList.contains('is-selected'));
    const variantId = selectedPack?.dataset.productVariantId;

    if (!variantId) return null;

    return {
      id: variantId,
      quantity: 1,
      price: parseInt(selectedPack.dataset.productVariantPrice, 10) || 0,
      compareAtPrice: parseInt(selectedPack.dataset.productVariantCompareAtPrice, 10) || parseInt(selectedPack.dataset.productVariantPrice, 10) || 0,
    };
  }

  #getSelectedPack(step) {
    return this.#getStepPacks(step).find((pack) => !pack.hidden && pack.classList.contains('is-selected')) || null;
  }

  #getSelectedPackAddonItems(step) {
    const selectedPack = this.#getSelectedPack(step);
    if (!selectedPack) return [];

    const cartProducts = selectedPack.querySelectorAll('[data-ref="bundle-builder-pack-cart-product"]');

    if (cartProducts.length > 0) {
      return Array.from(cartProducts)
        .map((entry) => ({
          id: entry.dataset.variantId,
          quantity: parseInt(entry.dataset.quantity, 10) || 0,
          properties: { _cure: 'true', _cure_scope: 'pack_addon' },
          price: parseInt(entry.dataset.price, 10) || 0,
          compareAtPrice:
            parseInt(entry.dataset.compareAtPrice, 10) || parseInt(entry.dataset.price, 10) || 0,
        }))
        .filter((item) => item.id && item.quantity > 0);
    }

    const variantId = selectedPack.dataset.additionalProductVariantId;
    const quantity = parseInt(selectedPack.dataset.additionalProductQuantity, 10) || 0;

    if (!variantId || quantity <= 0) return [];

    return [
      {
        id: variantId,
        quantity,
        properties: { _cure: 'true', _cure_scope: 'pack_addon' },
        price: parseInt(selectedPack.dataset.additionalProductPrice, 10) || 0,
        compareAtPrice:
          parseInt(selectedPack.dataset.additionalProductCompareAtPrice, 10) ||
          parseInt(selectedPack.dataset.additionalProductPrice, 10) ||
          0,
      },
    ];
  }

  #getSelectedStepEntries(step) {
    const stepType = step.dataset.stepType;
    const entries = [];
    const showCartProductPrefix = step.dataset.showCartProductPrefix === 'true';
    const cartProductPrefix = step.dataset.cartProductPrefix?.trim() || '';

    for (const item of this.#getStepItems(step)) {
      const itemTitle = item.dataset.productTitle?.trim();
      if (!itemTitle) continue;

      if (stepType === 'quantity_select') {
        const quantity = parseInt(item.dataset.quantity, 10) || 0;
        if (quantity > 0) {
          entries.push({
            title: itemTitle,
            quantity,
            stepType,
            cartProductPrefix: showCartProductPrefix ? cartProductPrefix : '',
          });
        }
        continue;
      }

      if (stepType === 'cross_sell') continue;

      if (item.dataset.selected === 'true') {
        entries.push({ title: itemTitle, quantity: 1, stepType });
      }
    }

    return entries;
  }

  #formatPropertyLines(entries) {
    const groupedEntries = new Map();

    for (const entry of entries) {
      const existingEntry = groupedEntries.get(entry.title);

      if (existingEntry) {
        existingEntry.quantity += entry.quantity;
        continue;
      }

      groupedEntries.set(entry.title, { ...entry });
    }

    return Array.from(groupedEntries.values()).map((entry) => {
      if (entry.stepType === 'quantity_select') {
        const prefixedTitle = entry.cartProductPrefix ? `${entry.cartProductPrefix} ${entry.title}` : entry.title;
        return `${entry.quantity}x ${prefixedTitle}`;
      }

      return entry.title;
    });
  }

  #shouldAddStepItemsAsProducts(step) {
    return step?.dataset.addProductsAsItems === 'true';
  }

  #getSelectedStepProductItems(step) {
    const stepType = step.dataset.stepType;
    const selectedItems = [];
  
    for (const item of this.#getStepItems(step)) {
      const variantId = item.dataset.variantId;
      if (!variantId) continue;
  
      const isFreeProduct = item.dataset.isFreeProduct === 'true';
  
      if (stepType === 'quantity_select') {
        const quantity = parseInt(item.dataset.quantity, 10) || 0;
        if (quantity > 0) {
          selectedItems.push({
            id: variantId,
            quantity,
            ...(isFreeProduct ? { properties: { _cure: 'true', _cure_scope: 'flacon' } } : {}),
          });
        }
        continue;
      }
  
      if (item.dataset.selected === 'true') {
        selectedItems.push({
          id: variantId,
          quantity: 1,
          ...(isFreeProduct ? { properties: { _cure: 'true', _cure_scope: 'flacon' } } : {}),
        });
      }
    }
  
    return selectedItems;
  }

  #selectPack(step, pack) {
    const targetQuantity = parseInt(pack.dataset.targetQuantity, 10) || 0;
    if (targetQuantity === 0) return;

    delete step.dataset.requiredPackQuantity;
    step.dataset.manualPackQuantity = String(targetQuantity);

    if (this.#packHasFlavorSelectors(pack) && this.#usesPackFlavorSelectors(step)) {
      this.#syncItemsFromPackFlavorSelectors(step, pack);
    }

    this.#syncState();
    document.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #syncPackSelection(step) {
    const packs = this.#getAvailableStepPacks(step);
    if (packs.length === 0) {
      delete step.dataset.manualPackQuantity;
      delete step.dataset.requiredPackQuantity;
      return null;
    }

    this.#clampStepSelectedQuantityToAvailablePacks(step);

    const selectedQuantity = this.#getStepSelectedQuantity(step);
    let requiredQuantity = this.#getStepRequiredQuantity(step);
    let selectedPack = null;

    if (requiredQuantity > 0 && !packs.some((pack) => (parseInt(pack.dataset.targetQuantity, 10) || 0) === requiredQuantity)) {
      delete step.dataset.manualPackQuantity;
      requiredQuantity = 0;
    }

    const sortedPacks = packs
      .slice()
      .sort((packA, packB) => (parseInt(packA.dataset.targetQuantity, 10) || 0) - (parseInt(packB.dataset.targetQuantity, 10) || 0));

    const selectionReferenceQuantity = Math.max(selectedQuantity, requiredQuantity);

    if (selectionReferenceQuantity > 0) {
      selectedPack =
        sortedPacks.find((pack) => (parseInt(pack.dataset.targetQuantity, 10) || 0) >= selectionReferenceQuantity) ||
        sortedPacks[sortedPacks.length - 1] ||
        null;
    }

    if (!selectedPack && requiredQuantity > 0) {
      selectedPack = sortedPacks.find((pack) => (parseInt(pack.dataset.targetQuantity, 10) || 0) === requiredQuantity) || null;
    }

    if (selectedPack) {
      step.dataset.requiredPackQuantity = String(parseInt(selectedPack.dataset.targetQuantity, 10) || 0);
    } else if (requiredQuantity > 0) {
      step.dataset.requiredPackQuantity = String(requiredQuantity);
    } else {
      delete step.dataset.requiredPackQuantity;
    }

    for (const pack of packs) {
      pack.classList.toggle('is-selected', pack === selectedPack);
      pack.setAttribute('aria-pressed', String(pack === selectedPack));
    }

    for (const pack of this.#getStepPacks(step)) {
      if (packs.includes(pack)) continue;
      pack.classList.remove('is-selected');
      pack.setAttribute('aria-pressed', 'false');
    }

    const effectiveRequiredQuantity = this.#getStepRequiredQuantity(step);
    const missingQuantity = Math.max(0, effectiveRequiredQuantity - selectedQuantity);
    return missingQuantity > 0 ? missingQuantity : null;
  }

  #syncHelperMessage(missingQuantity) {
    const helper = this.querySelector('[data-ref="bundle-builder-helper"]');
    if (!helper) return;

    if (!missingQuantity || missingQuantity <= 0) {
      helper.hidden = true;
      return;
    }

    const helperLabelTemplate = this.dataset.labelMoreFlavors || 'Add [count] more flavor(s) to continue';
    helper.hidden = false;
    helper.textContent = helperLabelTemplate.replace('[count]', String(missingQuantity));
  }

  #isStepComplete(step) {
    const stepType = step.dataset.stepType;
    const items = this.#getStepItems(step);

    if (stepType === 'single_select') {
      return items.some((item) => item.dataset.selected === 'true');
    }

    if (stepType === 'quantity_select') {
      if (this.#selectedPackUsesFlavorSelectors(step)) {
        const selectedPack = this.#getSelectedPack(step);
        const requiredQuantity = parseInt(selectedPack?.dataset.targetQuantity, 10) || 0;
        const selectedQuantity = this.#getPackFlavorSelections(selectedPack).length;

        return requiredQuantity > 0 && selectedQuantity >= requiredQuantity;
      }

      const selectedQuantity = items.reduce((total, item) => total + (parseInt(item.dataset.quantity, 10) || 0), 0);
      const requiredQuantity = this.#getStepRequiredQuantity(step);

      if (requiredQuantity > 0) {
        return selectedQuantity >= requiredQuantity;
      }

      return selectedQuantity > 0;
    }

    return items.some((item) => item.dataset.selected === 'true');
  }

  #syncState() {
    let previousRequiredStepsComplete = true;
    let helperMissingQuantity = null;

    this.#steps.forEach((step, index) => {
      const isComplete = this.#isStepComplete(step);
      const isRequired = step.dataset.required === 'true';
      const isLocked = this.#useAccordion && index > 0 && !previousRequiredStepsComplete;

      step.dataset.complete = String(isComplete);
      step.classList.toggle('is-locked', isLocked);
      const missingQuantity = this.#syncPackSelection(step);

      if (helperMissingQuantity === null && missingQuantity !== null) {
        helperMissingQuantity = missingQuantity;
      }

      if (this.#useAccordion && isLocked && step.open) {
        step.open = false;
      }

      if (isRequired) {
        previousRequiredStepsComplete = previousRequiredStepsComplete && isComplete;
      }
    });

    this.#syncHelperMessage(helperMissingQuantity);

    if (this.#useAccordion) {
      const hasOpenUnlockedStep = this.#steps.some((step) => step.open && !step.classList.contains('is-locked'));
      if (!hasOpenUnlockedStep) {
        const firstUnlockedStep = this.#steps.find((step) => !step.classList.contains('is-locked'));
        if (firstUnlockedStep) {
          this.#openStep(firstUnlockedStep);
        }
      }
    } else {
      this.#openAllSteps(false);
    }
  }

  isSelectionComplete() {
    return this.#steps.every((step) => {
      if (step.dataset.required !== 'true') return true;
      return this.#isStepComplete(step);
    });
  }

  getSelectionProgress() {
    for (const step of this.#steps) {
      if (step.dataset.required !== 'true') continue;
      if (step.dataset.stepType !== 'quantity_select') continue;

      const required = this.#getStepRequiredQuantity(step);
      if (required <= 0) continue;

      const selected = this.#getStepSelectedQuantity(step);
      if (selected >= required) continue;

      return { selected, required };
    }

    return null;
  }

  getSelectedItems() {
    const items = [];

    for (const step of this.#steps) {
      const selectedPackVariant = this.#getSelectedPackVariant(step);
      if (selectedPackVariant) {
        items.push({ id: selectedPackVariant.id, quantity: selectedPackVariant.quantity });
      }

      if (step.dataset.stepType === 'cross_sell' || this.#shouldAddStepItemsAsProducts(step)) {
        items.push(...this.#getSelectedStepProductItems(step));
      }
    }

    return items;
  }

  getSelectedAddonItems() {
    const items = [];

    for (const step of this.#steps) {
      items.push(...this.#getSelectedPackAddonItems(step));

      if (step.dataset.stepType === 'cross_sell' || this.#shouldAddStepItemsAsProducts(step)) {
        items.push(...this.#getSelectedStepProductItems(step));
      }
    }

    return items;
  }

  getSelectedProperties() {
    const properties = {};
    const lines = [];
    const trailingLines = [];

    this.#steps.forEach((step) => {
      const selectedPack = this.#getSelectedPack(step);
      const stepEntries = this.#getSelectedStepEntries(step);

      if (selectedPack?.dataset.packTitle) {
        properties._bundle_pack_duration = selectedPack.dataset.packTitle;
      }

      const fromMetafieldOrData = parseInt(selectedPack?.dataset.cureFlaconBudget, 10);
      const targetQty = parseInt(selectedPack?.dataset.targetQuantity, 10) || 0;
      const cureBudget = Number.isFinite(fromMetafieldOrData) && fromMetafieldOrData > 0 ? fromMetafieldOrData : targetQty;
      if (cureBudget > 0) {
        properties._cure_flacon_budget = String(cureBudget);
      }

      if (!this.#shouldAddStepItemsAsProducts(step) && stepEntries.length > 0) {
        lines.push(...this.#formatPropertyLines(stepEntries));
      }

      const packCartText = selectedPack?.dataset.cartText?.trim();
      if (packCartText) {
        trailingLines.push(packCartText);
      }
    });

    lines.push(...trailingLines);

    if (lines.length > 0) {
      properties._bundle_builder = 'true';

      lines.forEach((line, index) => {
        properties[`_bundle_line_${index + 1}`] = line;
      });
    }

    return properties;
  }

  getPrimaryCartItem(form) {
    const selectedPackVariant =
      this.#steps
        .map((step) => this.#getSelectedPackVariant(step))
        .find(Boolean) || null;
    const fallbackVariantId = form?.querySelector('input[name="id"]')?.value;
    const quantity = parseInt(form?.querySelector('input[name="quantity"]')?.value, 10) || 1;

    if (selectedPackVariant?.id) {
      return {
        id: selectedPackVariant.id,
        quantity,
      };
    }

    if (!fallbackVariantId) return null;

    return {
      id: fallbackVariantId,
      quantity,
    };
  }

  getSelectedTotalPrice() {
    let totalPrice = 0;

    for (const step of this.#steps) {
      const stepType = step.dataset.stepType;
      const selectedPackVariant = this.#getSelectedPackVariant(step);
      const shouldUsePackPrice = selectedPackVariant && (selectedPackVariant.price > 0 || stepType !== 'quantity_select');

      if (shouldUsePackPrice) {
        totalPrice += selectedPackVariant.price * selectedPackVariant.quantity;
      } else if (stepType === 'quantity_select') {
        for (const item of this.#getStepItems(step)) {
          const quantity = parseInt(item.dataset.quantity, 10) || 0;
          if (quantity <= 0) continue;

          totalPrice += this.#getItemEffectivePrice(item) * quantity;
        }
      }

      if (stepType === 'cross_sell' || this.#shouldAddStepItemsAsProducts(step)) {
        for (const selectedItem of this.#getSelectedStepProductItems(step)) {
          const matchingItem = this.#getStepItems(step).find((item) => item.dataset.variantId === String(selectedItem.id));
          totalPrice += this.#getItemEffectivePrice(matchingItem) * selectedItem.quantity;
        }
      }
    }

    return totalPrice;
  }

  getSelectedTotalCompareAtPrice() {
    let totalCompareAtPrice = 0;

    for (const step of this.#steps) {
      const stepType = step.dataset.stepType;
      const selectedPackVariant = this.#getSelectedPackVariant(step);
      const shouldUsePackCompareAtPrice =
        selectedPackVariant && (selectedPackVariant.compareAtPrice > 0 || stepType !== 'quantity_select');

      if (shouldUsePackCompareAtPrice) {
        totalCompareAtPrice += selectedPackVariant.compareAtPrice * selectedPackVariant.quantity;
      } else if (stepType === 'quantity_select') {
        for (const item of this.#getStepItems(step)) {
          const quantity = parseInt(item.dataset.quantity, 10) || 0;
          if (quantity <= 0) continue;

          totalCompareAtPrice += this.#getItemEffectiveCompareAtPrice(item) * quantity;
        }
      }

      if (stepType === 'cross_sell' || this.#shouldAddStepItemsAsProducts(step)) {
        for (const selectedItem of this.#getSelectedStepProductItems(step)) {
          const matchingItem = this.#getStepItems(step).find((item) => item.dataset.variantId === String(selectedItem.id));
          totalCompareAtPrice += this.#getItemEffectiveCompareAtPrice(matchingItem) * selectedItem.quantity;
        }
      }
    }

    return totalCompareAtPrice;
  }

  shouldReplaceMainProduct() {
    return true;
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
